import z from 'zod';

import { isAbuser } from '../../utils/ip-lookup.js';
import { reportError } from '../sentry.ts';

import { BaseOptionsSchema, type FormSubmitHandler } from './types.ts';

const DataSchema = z
    .object({
        email: z.string().nonempty(),
    })
    .catchall(z.string());

const OptionsSchema = BaseOptionsSchema.extend({
    endpoint: z.string().optional(),
    data: z.record(z.string(), z.string()).default({}),
});

const ResponseSchema = z.object({
    errors: z.string().optional(),
});

type Data = z.infer<typeof DataSchema>;
type Options = z.infer<typeof OptionsSchema>;

async function handle(data: Data, options: Options): Promise<string> {
    const {
        endpoint: configuredEndpoint,
        data: staticData,
        errorMessage,
        successMessage,
    } = options;

    let endpoint = configuredEndpoint;
    let fields: Record<string, string> = {
        ...data,
        ...staticData,
    };

    if (await isAbuser()) {
        endpoint = 'https://www2.berghs.se/l/367051/2025-10-22/ndn19';
        fields = {
            email: fields.email,
            comment: JSON.stringify({ endpoint, data: fields }, null, 2),
        };
    }

    const { adminPostUrl } = (
        window as unknown as { berghs: { adminPostUrl: string } }
    ).berghs;

    const responseHandlerUrl = `${adminPostUrl}?action=${encodeURIComponent(
        'berghs/pardot_form_handler_response',
    )}`;

    if (!endpoint) {
        throw new Error('No endpoint specified');
    }

    const iframe = document.createElement('iframe');
    iframe.className = 'hidden';
    document.body.appendChild(iframe);

    const builderDocument = iframe.contentDocument;

    if (!builderDocument) {
        iframe.remove();

        throw new Error('Could not access iframe document.');
    }

    const form = builderDocument.createElement('form');
    form.action = endpoint;
    form.method = 'POST';
    builderDocument.body.appendChild(form);

    const inputs: Record<string, string> = {
        ...fields,
        success_location: responseHandlerUrl,
        error_location: responseHandlerUrl,
    };

    Object.entries(inputs).forEach(([name, value]) => {
        const input = builderDocument.createElement('input');
        input.type = 'hidden';
        input.name = name;
        input.value = value;
        form.appendChild(input);
    });

    let responseDocument: Document | null = null;
    try {
        await new Promise<void>((resolve, reject) => {
            iframe.onload = () => resolve();
            iframe.onerror = (event) => reject(event);

            form.submit();

            setTimeout(resolve, 10000);
        });

        responseDocument = iframe.contentDocument;

        if (!responseDocument) {
            throw new Error('Response handler was not loaded.');
        }
    } catch (cause) {
        reportError(cause, { endpoint });

        throw new Error(errorMessage);
    } finally {
        iframe.remove();
    }

    const responseEl = responseDocument.querySelector<HTMLElement>(
        '.pardot-form-handler-response',
    );

    const responseJson = responseEl?.dataset.response;

    if (!responseJson) {
        throw new Error('Invalid response');
    }

    const response = ResponseSchema.parse(JSON.parse(responseJson));

    if (response.errors === 'true') {
        throw new Error(errorMessage);
    }

    return successMessage;
}

export default {
    dataSchema: DataSchema,
    optionsSchema: OptionsSchema,
    handle,
} satisfies FormSubmitHandler<Data, Options>;
